import { PlatformContextType } from "@wingspanhq/users/dist/lib/interfaces";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useQueryInternalAccounts } from "../../../../query/banking/queries/useQueryInternalAccounts";
import { useUserId } from "../../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useQueryCustomerEntity } from "../../../../query/onboarding/queries/useQueryCustomerEntity";
import { useUpdateActivity } from "../../../../query/users/mutations";
import { useActivities, useUserProfile } from "../../../../query/users/queries";
import { avatarWallet } from "../../../../shared/constants/avatars";
import { useWSAnalytics } from "../../../../utils/WSAnalytics";
import { Promotion, PromotionVariant } from "./Promotion";

const WALLET_PROMOTION_VARIANTS: PromotionVariant[] = [
  {
    avatar: avatarWallet,
    title: "Wingspan Wallet",
    quote: {
      icon: "bulb",
      title: "The free business account for freelancers",
      content: (
        <>
          Stop mixing business and personal funds. Get a free, FDIC-insured
          business checking account with no monthly fees or minimums.{" "}
          <b>
            Centralize your payments, spending, and bookkeeping in one place.
          </b>
        </>
      )
    },
    action: {
      label: "Open my free wallet"
    }
  },
  {
    avatar: avatarWallet,
    title: "Wingspan Wallet",
    quote: {
      icon: "gift",
      title: "Earn 1% cashback on business spending",
      content: (
        <>
          Your Wingspan Wallet comes with a Visa® Debit Card that earns{" "}
          <b>1% cashback on all purchases</b>. Every transaction is
          automatically tracked and categorized to help you find tax write-offs.
        </>
      )
    },
    action: {
      label: "Get my 1% cashback card"
    }
  },
  {
    avatar: avatarWallet,
    title: "Wingspan Wallet",
    quote: {
      icon: "in",
      title: "Get paid faster, for free",
      content: (
        <>
          Unlock free instant access to your earnings. While instant payouts to
          external accounts have a fee, transfers into your{" "}
          <b>Wingspan Wallet are always free and available immediately</b>.
        </>
      )
    },
    action: {
      label: "Activate free instant payouts"
    }
  }
];

export const useShowWalletPromotion = () => {
  const userId = useUserId();
  const queryInternalAccounts = useQueryInternalAccounts();
  const queryActivity = useActivities(userId);
  const queryMonitoredEntity = useQueryCustomerEntity();
  const queryUser = useUserProfile(userId);
  const queryFeatureFlags = useFeatureFlags();

  return useMemo(() => {
    if (
      !queryFeatureFlags.data ||
      !queryFeatureFlags.data.walletDashboardPromotion
    ) {
      return false;
    }

    if (
      !queryActivity.data ||
      queryActivity.data.events.dashboardWalletsPromotionDismissed
    ) {
      return false;
    }

    const hasPayeeContext = queryUser.data?.contexts?.some(
      context => context.type === PlatformContextType.Payee
    );

    if (!hasPayeeContext) {
      return false;
    }

    if (queryInternalAccounts.isLoading) {
      return false;
    }

    if (queryMonitoredEntity.data?.country !== "US") {
      return false;
    }

    const hasAnyBankingAccount = (queryInternalAccounts.data || []).find(
      account => account.type === "Banking"
    );

    return !hasAnyBankingAccount;
  }, [
    queryActivity.data,
    queryFeatureFlags.data,
    queryInternalAccounts.data,
    queryInternalAccounts.isLoading,
    queryMonitoredEntity.data?.country,
    queryUser.data?.contexts
  ]);
};

export const CardWalletPromotion = () => {
  const userId = useUserId();
  const history = useHistory();
  const analytics = useWSAnalytics();
  const showWalletPromotion = useShowWalletPromotion();

  const [dismiss, dismissMeta] = useUpdateActivity(userId);

  const handleDismiss = () => {
    dismiss({
      events: {
        dashboardWalletsPromotionDismissed: true
      }
    });
  };

  // Instantly hide promotion while updating activity
  if (dismissMeta.isLoading) {
    return null;
  }

  if (!showWalletPromotion) {
    return null;
  }

  return (
    <Promotion
      key="wallet"
      variants={WALLET_PROMOTION_VARIANTS}
      onDismiss={handleDismiss}
      onAction={variantIndex => {
        analytics.track.other("Dashboard Wallet Promotion Click", {
          variantIndex
        });

        history.push("/member/activate-banking");
      }}
    />
  );
};
